import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import { getToken } from '@/utils/auth'

Vue.use(VueRouter)

// 布局组件
import Layout from '@/layout'

/**
 * 路由配置
 * hidden: true                   // 在侧边栏隐藏该路由
 * alwaysShow: true               // 总是显示根菜单
 * meta: {
 *   title: '标题'                // 菜单标题
 *   icon: 'icon-name'            // 图标
 *   noCache: true                // 不缓存
 *   affix: true                  // 固定在tags-view
 *   roles: ['admin', 'editor']   // 权限
 * }
 */

// 公共路由
export const constantRoutes = [
  {
    path: '/login',
    component: () => import('@/views/login/index'),
    hidden: true
  },
  {
    path: '/404',
    component: () => import('@/views/error/404'),
    hidden: true
  },
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/index'),
        meta: { title: '首页', icon: 'el-icon-s-home', affix: true }
      }
    ]
  }
]

// 动态路由
export const asyncRoutes = [
  // 游戏管理
  {
    path: '/game',
    component: Layout,
    children: [
      {
        path: 'list',
        name: 'GameList',
        component: () => import('@/views/game/index'),
        meta: { title: '游戏管理', icon: 'el-icon-s-platform' }
      }
    ]
  },

  // 商品管理
  {
    path: '/product',
    component: Layout,
    redirect: '/product/list',
    meta: { title: '商品管理', icon: 'el-icon-goods' },
    children: [
      {
        path: 'list',
        name: 'ProductList',
        component: () => import('@/views/product/index'),
        meta: { title: '商品列表', icon: 'el-icon-s-order' }
      },
      {
        path: 'audit',
        name: 'ProductAudit',
        component: () => import('@/views/product/audit'),
        meta: { title: '商品审核', icon: 'el-icon-s-check' }
      }
    ]
  },

  // 用户管理
  {
    path: '/user',
    component: Layout,
    redirect: '/user/list',
    children: [
      {
        path: 'list',
        name: 'UserList',
        component: () => import('@/views/user/index'),
        meta: { title: '用户管理', icon: 'el-icon-user' }
      }
    ]
  },

  // 订单管理
  {
    path: '/order',
    component: Layout,
    children: [
      {
        path: 'list',
        name: 'OrderList',
        component: () => import('@/views/order/index'),
        meta: { title: '订单管理', icon: 'el-icon-s-order' }
      }
    ]
  },

  // 提现管理
  {
    path: '/withdraw',
    component: Layout,
    redirect: '/withdraw/user',
    meta: { title: '提现管理', icon: 'el-icon-wallet' },
    children: [
      {
        path: 'user',
        name: 'UserWithdraw',
        component: () => import('@/views/withdraw/user'),
        meta: { title: '用户提现', icon: 'el-icon-user' }
      },
      {
        path: 'service',
        name: 'ServiceWithdraw',
        component: () => import('@/views/withdraw/service'),
        meta: { title: '客服提现', icon: 'el-icon-service' }
      }
    ]
  },

  // 内容管理
  {
    path: '/content',
    component: Layout,
    redirect: '/content/banner',
    meta: { title: '内容管理', icon: 'el-icon-document' },
    children: [
      {
        path: 'banner',
        name: 'Banner',
        component: () => import('@/views/banner/index'),
        meta: { title: '轮播图管理', icon: 'el-icon-picture' }
      },
      {
        path: 'announcement',
        name: 'Announcement',
        component: () => import('@/views/announcement/index'),
        meta: { title: '公告管理', icon: 'el-icon-bell' }
      },
      {
        path: 'case',
        name: 'Case',
        component: () => import('@/views/case/index'),
        meta: { title: '案例管理', icon: 'el-icon-files' }
      },
      {
        path: 'business',
        name: 'Business',
        component: () => import('@/views/business/index'),
        meta: { title: '业务介绍', icon: 'el-icon-s-opportunity' }
      }
    ]
  },

  // 系统配置
  {
    path: '/system',
    component: Layout,
    redirect: '/system/admin',
    meta: { title: '系统配置', icon: 'el-icon-setting' },
    children: [
      {
        path: 'admin',
        name: 'AdminManage',
        component: () => import('@/views/admin/index'),
        meta: { title: '管理员管理', icon: 'el-icon-user-solid', roles: [1] }
      },
      {
        path: 'blacklist',
        name: 'Blacklist',
        component: () => import('@/views/blacklist/index'),
        meta: { title: '黑号库管理', icon: 'el-icon-warning' }
      },
      {
        path: 'service',
        name: 'Service',
        component: () => import('@/views/service/index'),
        meta: { title: '客服管理', icon: 'el-icon-service' }
      },
      {
        path: 'assignment-rule',
        name: 'AssignmentRule',
        component: () => import('@/views/assignment-rule/index'),
        meta: { title: '客服分配规则', icon: 'el-icon-s-operation' }
      },
      {
        path: 'insurance',
        name: 'Insurance',
        component: () => import('@/views/insurance/index'),
        meta: { title: '包赔配置', icon: 'el-icon-s-claim' }
      },
      {
        path: 'config',
        name: 'Config',
        component: () => import('@/views/config/index'),
        meta: { title: '配置管理', icon: 'el-icon-s-tools' }
      },
      {
        path: 'operation-log',
        name: 'OperationLog',
        component: () => import('@/views/operation-log/index'),
        meta: { title: '操作日志', icon: 'el-icon-document' }
      }
    ]
  },

  // 消息管理
  {
    path: '/message',
    component: Layout,
    redirect: '/message/batch-send',
    children: [
      {
        path: 'batch-send',
        name: 'BatchSendMessage',
        component: () => import('@/views/message/batch-send'),
        meta: { title: '批量发消息', icon: 'el-icon-chat-dot-round' }
      }
    ]
  },

  // 第三方对接
  {
    path: '/api-integration',
    component: Layout,
    redirect: '/api-integration/accounts',
    meta: { title: '第三方对接', icon: 'el-icon-connection' },
    children: [
      {
        path: 'accounts',
        name: 'ApiAccountManage',
        component: () => import('@/views/api-account/index'),
        meta: { title: 'API账号管理', icon: 'el-icon-key' }
      },
      {
        path: 'suppliers',
        name: 'ApiSupplierManage',
        component: () => import('@/views/api-supplier/index'),
        meta: { title: '号商管理', icon: 'el-icon-user' }
      }
    ]
  },

  // 404必须放在最后
  { path: '*', redirect: '/404', hidden: true }
]

const createRouter = () => new VueRouter({
  mode: 'history',
  scrollBehavior: () => ({ y: 0 }),
  routes: constantRoutes
})

const router = createRouter()

// 重置路由
export function resetRouter() {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher
}

// 路由守卫
router.beforeEach(async (to, from, next) => {
  const hasToken = getToken()

  if (hasToken) {
    if (to.path === '/login') {
      next({ path: '/' })
    } else {
      const hasUserInfo = store.getters.userInfo
      const hasRoles = store.getters.permission_routes && store.getters.permission_routes.length > 0

      if (hasUserInfo && hasRoles) {
        next()
      } else {
        try {
          // 获取用户信息
          if (!hasUserInfo) {
            await store.dispatch('user/getInfo')
          }

          // 生成可访问的路由表
          const accessRoutes = await store.dispatch('permission/generateRoutes')

          // 动态添加路由
          router.addRoutes(accessRoutes)

          // hack方法 确保addRoutes已完成
          next({ ...to, replace: true })
        } catch (error) {
          await store.dispatch('user/resetToken')
          next(`/login?redirect=${to.path}`)
        }
      }
    }
  } else {
    if (to.path === '/login') {
      next()
    } else {
      next(`/login?redirect=${to.path}`)
    }
  }
})

// 捕获路由错误
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err)
}

export default router